import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import {
  faGear,
  faUser,
  faCog,
  // faBasketShopping, // Service Catalog menu entry temporarily hidden
  faCircleQuestion,
  faRightFromBracket,
  faChevronLeft,
  faBars,
} from "@fortawesome/pro-light-svg-icons";
import { useGetMeQuery } from "../../services/ScopeStackAPI";

interface UserMenuCardProps {
  expanded: boolean;
  onToggle: () => void;
  /** When true (inside a chrome root that carries the /users route),
   *  the User Profile link navigates client-side instead of full-GET. */
  clientSideNav?: boolean;
}

const menuLinks = [
  {
    label: "User Profile",
    href: "/users/edit",
    icon: faUser,
    id: "user-profile",
  },
  {
    label: "Settings",
    href: "/admin/sections",
    icon: faCog,
    id: "account-settings",
  },
  // Service Catalog temporarily hidden from the user menu.
  // {
  //   label: "Service Catalog",
  //   href: "/catalog_items",
  //   icon: faBasketShopping,
  //   id: "service-catalog",
  // },
  {
    label: "Help Center",
    href: "https://support.scopestack.io/help",
    icon: faCircleQuestion,
    id: "help-center",
    external: true,
  },
  {
    label: "Sign Out",
    href: "/users/sign_out",
    icon: faRightFromBracket,
    id: "signout",
    method: "delete" as const,
  },
];

const linkStyle: React.CSSProperties = {
  color: "rgba(255,255,255,0.7)",
  textDecoration: "none",
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13,
  fontFamily: "Open Sans, sans-serif",
  borderRadius: 4,
  transition: "background-color 0.15s ease",
};

const UserMenuCard: React.FC<UserMenuCardProps> = ({
  expanded,
  onToggle,
  clientSideNav = false,
}) => {
  const [open, setOpen] = useState(false);
  const { data: currentUser } = useGetMeQuery();
  const navigate = useNavigate();

  const userName = currentUser?.attributes?.name || "";
  const meUserId = currentUser?.id;
  const profileHref = meUserId
    ? `/users/${meUserId}/edit/contact`
    : "/users/edit";

  const handleSignOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/users/sign_out";
    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "delete";
    form.appendChild(methodInput);
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,0.1)",
        marginTop: "auto",
      }}
    >
      {/* Card body */}
      {open && (
        <div style={{ padding: "8px 4px" }}>
          {menuLinks.map((link) => {
            const isSignOut = link.id === "signout";
            const isProfile = link.id === "user-profile";
            const href = isProfile ? profileHref : link.href;
            const handleClick = isSignOut
              ? handleSignOut
              : isProfile && clientSideNav && meUserId
              ? (e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (
                    e.metaKey ||
                    e.ctrlKey ||
                    e.shiftKey ||
                    e.altKey ||
                    e.button !== 0
                  )
                    return;
                  e.preventDefault();
                  navigate(profileHref);
                  setOpen(false);
                }
              : undefined;

            const anchor = (
              <a
                key={link.id}
                href={href}
                id={link.id}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noopener noreferrer" : undefined}
                onClick={handleClick}
                style={{
                  ...linkStyle,
                  justifyContent: expanded ? "flex-start" : "center",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "rgba(255,255,255,0.05)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "transparent";
                }}
              >
                <FontAwesomeIcon
                  icon={link.icon}
                  style={{ width: 16, fontSize: 14, flexShrink: 0 }}
                />
                {expanded && <span>{link.label}</span>}
              </a>
            );

            if (expanded) return anchor;

            return (
              <OverlayTrigger
                key={link.id}
                placement="right"
                overlay={
                  <Tooltip id={`tooltip-${link.id}`}>{link.label}</Tooltip>
                }
              >
                {anchor}
              </OverlayTrigger>
            );
          })}
        </div>
      )}

      {/* Card header */}
      <div
        style={{
          padding: expanded ? "12px 16px" : "12px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          gap: 8,
        }}
      >
        {expanded && (
          <span
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "Open Sans, sans-serif",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {userName}
          </span>
        )}
        <button
          onClick={() => setOpen((prev) => !prev)}
          aria-label="User menu"
          style={{
            background: "none",
            border: "none",
            color: open ? "#63C4AB" : "rgba(255,255,255,0.5)",
            cursor: "pointer",
            padding: 6,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon
            icon={faGear}
            style={{ fontSize: expanded ? 16 : 20 }}
          />
        </button>
      </div>

      {/* Card footer — collapse toggle */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          padding: "12px 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <button
          onClick={onToggle}
          aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            cursor: "pointer",
            padding: "6px 12px",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            fontFamily: "Open Sans, sans-serif",
          }}
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronLeft : faBars}
            style={{ fontSize: expanded ? 14 : 18 }}
          />
          {expanded && (
            <span style={{ color: "rgba(255,255,255,0.5)" }}>Collapse</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default UserMenuCard;
